import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import awsExports from "aws-exports";
import { axiosInstance } from "providers/rest-data-provider/utils";

const API_BASE_URL = awsExports.endpoints[0].endpoint;

type FundType = "DAF" | "DESIGNATED";

interface ResultDialog {
  open: boolean;
  title: string;
  message: string;
}

interface ConfirmState {
  fundType: FundType;
  existing: boolean;
  referenceNumber: string;
}

const CLOSED_RESULT_DIALOG: ResultDialog = { open: false, title: "", message: "" };

const errorMessageFrom = (error: any) =>
  `Create Fund in Sage Intacct failed: ${
    error?.response?.data?.message ??
    error?.message ??
    "An unexpected error occurred."
  }`;

interface ModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fundAccountId: number;
  refetch: () => void;
}

export const SyncFundToSageModal: React.FC<ModalProps> = ({
  open,
  setOpen,
  fundAccountId,
  refetch,
}) => {
  const [pendingFundType, setPendingFundType] = useState<FundType | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [resultDialog, setResultDialog] = useState<ResultDialog>(CLOSED_RESULT_DIALOG);
  const isPreviewing = pendingFundType !== null;

  const handleClose = () => {
    setOpen(false);
  };

  const handleChooseFundType = async (fundType: FundType) => {
    setPendingFundType(fundType);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/funds/${fundAccountId}/sync-sage/preview`,
        { fundType }
      );
      const result = response.data;
      setOpen(false);

      if (!result.success) {
        setResultDialog({
          open: true,
          title: "Create Fund Failed",
          message: result.message,
        });
        return;
      }

      setConfirmState({
        fundType,
        existing: Boolean(result.existing),
        referenceNumber: result.referenceNumber,
      });
    } catch (error: any) {
      setOpen(false);
      setResultDialog({
        open: true,
        title: "Create Fund Failed",
        message: errorMessageFrom(error),
      });
    } finally {
      setPendingFundType(null);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmState(null);
  };

  const handleConfirmSync = async () => {
    if (!confirmState) return;
    setIsConfirming(true);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/funds/${fundAccountId}/sync-sage`,
        {
          fundType: confirmState.fundType,
          referenceNumber: confirmState.referenceNumber,
        }
      );
      const result = response.data;
      setConfirmState(null);

      if (!result.success) {
        setResultDialog({
          open: true,
          title: "Create Fund Failed",
          message: result.message,
        });
        return;
      }

      setResultDialog({
        open: true,
        title: result.matchType === "name_match" ? "Fund Already Exists" : "Fund Created",
        message: result.message,
      });
      refetch();
    } catch (error: any) {
      setConfirmState(null);
      setResultDialog({
        open: true,
        title: "Create Fund Failed",
        message: errorMessageFrom(error),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Fund to Sage Intacct</DialogTitle>
        <DialogContent>
          <DialogContentText>
            How should this fund be created in Sage Intacct?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => handleChooseFundType("DAF")}
            disabled={isPreviewing}
            startIcon={
              pendingFundType === "DAF" && (
                <CircularProgress size={16} color="inherit" />
              )
            }
          >
            Create Fund as DAF
          </Button>
          <Button
            variant="contained"
            onClick={() => handleChooseFundType("DESIGNATED")}
            disabled={isPreviewing}
            startIcon={
              pendingFundType === "DESIGNATED" && (
                <CircularProgress size={16} color="inherit" />
              )
            }
          >
            Create Fund as Designated Fund
          </Button>
          <Button onClick={handleClose} disabled={isPreviewing}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmState !== null}
        onClose={handleCancelConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmState?.existing ? "Fund Already Exists" : "Create Fund"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmState?.existing
              ? `A fund with the same name already exists in Sage Intacct. It's reference number is '${confirmState.referenceNumber}'. Would you like to save it to this fund?`
              : `This fund will be created in SI as '${confirmState?.referenceNumber}'. Proceed?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmSync}
            disabled={isConfirming}
            startIcon={isConfirming && <CircularProgress size={16} color="inherit" />}
          >
            Yes
          </Button>
          <Button onClick={handleCancelConfirm} disabled={isConfirming} autoFocus>
            No
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resultDialog.open}
        onClose={() => setResultDialog(CLOSED_RESULT_DIALOG)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{resultDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{resultDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultDialog(CLOSED_RESULT_DIALOG)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
